import { type MessageCardVariant } from "@dalmatech/ui"
import { i18n } from "@/modules/App/components/i18n/i18n.client"
import { Pet } from "@/shared/types/global-store"
import { SyncBackPet } from "@/shared/types/lead-api-types"
import { IS_DEFFERED_SUBSCRIPTION_ACTIVE } from "@/shared/utils/flag-features"
import {
  differenceInMonths,
  differenceInYears,
  format,
  formatDuration,
  intervalToDuration,
  isAfter,
  parse,
} from "date-fns"
import deLocale from "date-fns/locale/de"
import enLocale from "date-fns/locale/en-GB"
import * as R from "ramda"
import { Trans } from "react-i18next"
import React from "react"

/** @returns label value array of month */
export function generateMonthsOptions(local = "en-EN") {
  return Array.from({ length: 12 }, (_, index) => {
    return {
      label: new Date(0, index).toLocaleString(local, { month: "long" }),
      value: String(index + 1),
    }
  })
}

/** @returns label value array of years starting form 2003 to now */
export function generateYearsOptions() {
  const currentYear = new Date().getFullYear()
  // TODO: change this, I add it ramdomly
  const startYear = 2003

  const years = Array.from({ length: currentYear - startYear + 1 }, (_, index) =>
    (startYear + index).toString()
  )

  return years
    .map((year) => ({
      label: year,
      value: year,
    }))
    .reverse()
}

export function getPetBirthday(birthMonth: string, birthYear: string) {
  const birthDate = parse(`${birthMonth}-01-${birthYear}`, "M-d-yyyy", new Date())
  const formattedBirthDate = format(birthDate, "yyyy-MM-dd")

  return { birthDate, formattedBirthDate }
}

/** @returns age in years */
export const calculateAgeInYears = (birthDate: Date, currentDate: Date): number => {
  return differenceInYears(currentDate, birthDate)
}

/** @returns age in months */
export const calculateAgeInMonths = (birthDate: Date, currentDate: Date): number => {
  return differenceInMonths(currentDate, birthDate)
}

/** @return true if pet is too young */
export const isPetTooYoung = (
  birthDate: Date | undefined,
  currentDate: Date | undefined
): boolean | undefined => {
  if (!birthDate || !currentDate) {
    return undefined
  }
  const ageInYears = calculateAgeInYears(birthDate, currentDate)
  const ageInMonths = calculateAgeInMonths(birthDate, currentDate)

  return ageInYears === 0 && ageInMonths < 2
}

/** @returns true if pet is too old */
export const isPetTooOld = (
  birthDate: Date | undefined,
  currentDate: Date | undefined
): boolean | undefined => {
  if (!birthDate || !currentDate) {
    return undefined
  }
  const ageInYears = calculateAgeInYears(birthDate, currentDate)

  return ageInYears > 8
}

/** @returns true if birthdate is after now */
export const checkIsAfterPetBirthDate = (birthMonth: string, birthYear: string): boolean => {
  if (!birthMonth || !birthYear) {
    return false
  }
  const { birthDate } = getPetBirthday(birthMonth, birthYear)
  const currentDate = new Date()
  return isAfter(birthDate!, currentDate)
}

/** @returns true if it's a valid date */
export const matchIsValidDate = (
  birthMonth: string | undefined,
  birthYear: string | undefined
): "idle" | "valid" | "invalid" | "valid-with-pet-young" => {
  if (!birthMonth || !birthYear) {
    return "idle"
  }
  const currentDate = new Date()
  const { birthDate } = getPetBirthday(birthMonth, birthYear)

  if (checkIsAfterPetBirthDate(birthMonth, birthYear)) {
    return "invalid"
  }

  if (isPetTooOld(birthDate, currentDate)) {
    return "invalid"
  }

  if (isPetTooYoung(birthDate, currentDate)) {
    return IS_DEFFERED_SUBSCRIPTION_ACTIVE ? "valid-with-pet-young" : "invalid"
  }

  return "valid"
}

export function checkForAlert(validPets?: Partial<SyncBackPet>[]): boolean {
  if (!validPets?.length) return false

  const today = new Date()

  const agesInMonths = validPets.map((pet) => {
    if (!pet?.birthday) {
      return 0
    }

    return calculateAgeInMonths(new Date(pet.birthday), today)
  })

  const oldestPetAgeInMonths = Math.max(...agesInMonths)

  const hasYoungPet = validPets.some((pet) => {
    if (!pet?.birthday) return false
    return isPetTooYoung(new Date(pet.birthday), today)
  })

  return hasYoungPet && oldestPetAgeInMonths >= 105
}

export function checkForAlertPetBirthdate(validPets?: Partial<SyncBackPet>[]): boolean {
  if (!validPets?.length) return false

  const today = new Date()
  let hasYoung = false
  let hasOld = false

  validPets.forEach((pet) => {
    if (pet?.birthday) {
      const birthDate = new Date(pet.birthday)
      const ageInMonths =
        (today.getFullYear() - birthDate.getFullYear()) * 12 +
        today.getMonth() -
        birthDate.getMonth()

      if (ageInMonths < 2) {
        hasYoung = true
      }
      if (ageInMonths >= 105) {
        hasOld = true
      }
    }
  })

  return hasYoung && hasOld
}

/** @returns parsed Date from string "yyyy-MM-dd" */
export function parsePetBirthday(date: string): Date {
  const parsedDate = parse(date, "yyyy-MM-dd", new Date())
  parsedDate.setHours(0, 0, 0, 0)
  parsedDate.setDate(1)
  return parsedDate
}

/** @returns formatted year and month string from month and year params */
export function getYearAndMonth(birthMonth: string, birthYear: string): string {
  const currentDate = new Date()
  currentDate.setDate(1)

  const birthDate = parse(`${birthMonth}-01-${birthYear}`, "M-d-yyyy", currentDate)

  return formatDuration(
    intervalToDuration({
      start: birthDate,
      end: currentDate,
    }),
    {
      format: ["years", "months"],
      locale: i18n.language === "de" ? deLocale : enLocale,
      delimiter: i18n.language === "de" ? " und " : " and ",
    }
  )
}

export type AgeSegment = "future" | "<2m" | "2m-12m" | "1y-8y" | ">8y"

function startOfMonthNoTime(d: Date) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  x.setDate(1)
  return x
}

function getAgeSegmentByDates(birthDate: Date, currentDate: Date): AgeSegment {
  const normBirth = startOfMonthNoTime(birthDate)
  const normNow = startOfMonthNoTime(currentDate)

  if (isAfter(normBirth, normNow)) return "future"

  const months = calculateAgeInMonths(normBirth, normNow)
  const years = calculateAgeInYears(normBirth, normNow)

  if (years > 8) return ">8y"
  if (years === 0 && months < 2) return "<2m"
  if (months >= 2 && months < 12) return "2m-12m"
  return "1y-8y"
}

export function getAgeSegment(birthMonth: string, birthYear: string): AgeSegment {
  const { birthDate } = getPetBirthday(birthMonth, birthYear)
  return getAgeSegmentByDates(birthDate, new Date())
}

/** @returns the age-based info card content (title + description) for the pet birthdate page */
export function getPetBirthdateCardContent(
  birthMonth: string,
  birthYear: string,
  petSex: Pet["sexe"],
  petName: Pet["name"]
): { title: string | null; description: React.ReactNode; variant: MessageCardVariant } {
  const { birthDate } = getPetBirthday(birthMonth, birthYear)
  const segment = getAgeSegment(birthMonth, birthYear)

  if (segment === "future") {
    return {
      title: null,
      description: i18n.t("pet-birthdate-errors.future-birthdate", { ns: "pet-birthdate-page" }),
      variant: "error"
    }
  }

  if (segment === ">8y") {
    return {
      title: null,
      description: i18n.t("pet-birthdate-errors.pet-is-too-old", { ns: "pet-birthdate-page" }),
      variant: "error"
    }
  }

  if (segment === "<2m") {
    const startAtTwoMonths = new Date(birthDate)
    startAtTwoMonths.setMonth(startAtTwoMonths.getMonth() + 2)
    startAtTwoMonths.setDate(1)
    const formattedStart = format(startAtTwoMonths, "dd/MM/yyyy")

    return {
      title: null,
      description: (
        <Trans
          i18n={i18n}
          ns="pet-birthdate-page"
          i18nKey={
            IS_DEFFERED_SUBSCRIPTION_ACTIVE
              ? "pet-birthdate-errors.pet-is-too-young"
              : "pet-birthdate-errors.pet-is-too-young-error"
          }
          values={{
            pet_name: petName,
            eventual_start_date: formattedStart,
          }}
          components={{
            b: <strong />,
            br: <br />,
          }}
        />
      ),
      variant: IS_DEFFERED_SUBSCRIPTION_ACTIVE ? "default" : "error"
    }
  }

  if (segment === "2m-12m") {
    return {
      title: i18n.t(`pet-birthdate-info-card-title-two-months-one-year-${petSex.toLowerCase()}`, {
        ns: "pet-birthdate-page",
      }),
      description: i18n.t("pet-birthdate-info-card-two-months-one-year", {
        ns: "pet-birthdate-page",
        pet_name: petName,
      }),
      variant: "info"
    }
  }

  return {
    title: i18n.t(`pet-birthdate-info-card-title-${petSex.toLowerCase()}`, {
      ns: "pet-birthdate-page",
      pet_name: petName,
    }),
    description: i18n.t("pet-birthdate-info-card-subtitle", {
      ns: "pet-birthdate-page",
      pet_name: petName,
    }),
    variant: "info"
  }
}

export function getPetById(
  pets: Partial<SyncBackPet>[],
  petId?: string | null
): Partial<SyncBackPet> | null {
  if (!pets || !petId) {
    return null
  }
  // @ts-expect-error this is an expected error as id isn't in backend type
  return R.find(R.propEq(petId, "id"))(pets || [])
}

export function getPetIndex(pets: Partial<SyncBackPet>[], petId?: string | null): number {
  // @ts-expect-error this is an expected error as id isn't in backend type
  return R.findIndex(R.propEq(petId, "id"))(pets || [])
}

export function resetPetPricing(
  pets: Partial<SyncBackPet>[],
  petIndex: number
): Partial<SyncBackPet>[] {
  if (!pets || petIndex < 0) {
    return pets || []
  }

  return pets.map((pet, index) => {
    if (index !== petIndex) {
      return pet
    }

    return {
      ...pet,
      pricing: undefined,
      price: undefined,
      after_discount: undefined,
      discount_value: undefined,
    }
  })
}
